import React, { useEffect, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Constants } from "./utils/constants";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/lib/integration/react";
import configureStore from "./redux/configureStore";
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import ApprovedHosts from "./screens/hosts/ApprovedHosts";
import CompanyApprovals from "./screens/hosts/CompanyApprovals";
import * as localService from "./services/localService";
import logger from "./utils/logger";
import Toaster from "./screens/settings/components/alerts/Toaster";
import { initPostHog, identifyUser } from "./utils/posthog";
import { AnalyticsProvider } from "./components/Analytics";
import { getAppFlavor, getCanonicalWebDomain } from "./utils/deepLink";
import "bootstrap/dist/css/bootstrap.min.css";
import "./layout/assets/sass/style.scss";
import "./index.scss";
import "./shadcn/shadcn-font.css";
import GlobalStyles from "./styles/GlobalStyles";
import { AuthLayout } from "./components/layouts/AuthLayout";
import RoleBasedRoute from "./components/auth/RoleBasedRoute";
import NotFoundPage from "./screens/404";
// Import last to override everything else
import "./force-montserrat.css";
// Import the JavaScript that forces Montserrat
import applyMontserratToAllElements from "./force-montserrat.js";

// Public routes
import LandingWeb from "./screens/landing-web";
import NewHomeScreen from "./screens/new-home";
import WorkLandingScreen from "./screens/work-landing";
import StayLandingScreen from "./screens/stay-landing";
import PlayLandingScreen from "./screens/play-landing";
import MoveLandingScreen from "./screens/move-landing";
import EatLandingScreen from "./screens/eat-landing";
import PricingLandingScreen from "./screens/pricing-landing";
import EnterpriseReadyScreen from "./screens/enterprise-ready";
import PlatformLandingScreen from "./screens/platform-landing";
import HostLandingWeb from "./screens/host-landing-web";
import SpaceDetailPage from "./screens/space-detail";
import HostProfilePage from "./screens/host-profile";
import WorkspacesSearch from "./screens/workspaces-search";
import ExperienceDetail from "./screens/experience-detail";
import AfricaTechFestivalPage from "./screens/africa-tech-festival";
// import About from "./screens/main/About";
// import FAQs from "./components/FAQs";
import ReportIssue from "./screens/ReportIssue";
import SignUp from "./screens/auth/signup-web";
import Confirm from "./screens/auth/confirm";
import Login from "./screens/auth/login-web";
import ResetPassword from "./screens/auth/ResetPassword";
import PasswordRotation from "./screens/auth/PasswordRotation";
import SavePassword from "./screens/auth/SavePassword";
import GoToMail from "./screens/auth/GoToMail";
import ConfirmInvite from "./screens/auth/ConfirmInvite";
import PrivacyPolicy from "./screens/Legal/PrivacyPolicy";
import TermsofUse from "./screens/Legal/Terms";
import CorporatePage from "./screens/corporate-page";
import PackageSignup from "./screens/auth/package-signup";
import OTPConfirmation from "./screens/auth/otp-confirmation";
import SignupSuccess from "./screens/auth/signup-success";
import SubscriberPricingPage from "./screens/subscriber-pricing";
import SubscriberSubscriptionUpgrade from "./screens/subscriber-subscription-upgrade";
import PaymentRedirect from "./screens/payment-redirect";
import QRCodeScreen from "./screens/qr-code";
import AppRedirectScreen from "./screens/app-redirect";
import QRDemoScreen from "./screens/qr-demo";
import BookingDetails from "./screens/booking-details";
import VerifyEmail from "./screens/auth/verify-email";

// Authenticated routes
import Dashboard from "./screens/dashboard";
import Properties from "./screens/properties/Properties";
import UserManagement from "./screens/user-management";
import Settings from "./screens/settings/Settings";
import Billing from "./screens/billing/Billing";
import BillingManagement from "./screens/billing-management";
import Booking from "./screens/booking/Booking";
import DisabledHosts from "./screens/hosts/DisabledHosts";
import SuspendedHosts from "./screens/hosts/SuspendedHosts";
import ApiExplorer from "./screens/developer/ApiExplorer";
import StartBooking from "./screens/start-booking";
import Orders from "./screens/orders";
import Packages from "./screens/packages";
import Announcements from "./screens/announcements";
import FilterProperties from "./screens/properties/FilterProperties";
import MembershipPlan from "./screens/membership-plan";
import PackageOrderSummary from "./screens/package-order-summary";
import RolesAndPermissions from "./screens/roles-permissions";

// Host Experience routes
import HostDashboard from "./screens/host-experience/host-dashboard";
import HostAnalytics from "./screens/host-experience/host-analytics";
import HostAllSpaces from "./screens/host-experience/host-all-spaces";
import HostClients from "./screens/host-experience/host-clients";
import HostCalendar from "./screens/host-experience/host-calendar";
import HostMessages from "./screens/host-experience/host-messages";
import HostCommunity from "./screens/host-experience/host-community";
import HostInvoice from "./screens/host-experience/host-invoice";
import HostInvoiceList from "./screens/host-experience/host-invoice/List";
import HostNotifications from "./screens/host-experience/host-notifications";
import HostSettings from "./screens/host-experience/host-settings";
import SettingsNotification from "./screens/host-experience/host-settings/SettingsNotification";
import AddInvoice from "./screens/host-experience/host-invoice/AddInvoice";
import ViewInvoice from "./screens/host-experience/host-invoice/ViewInvoice";
// import FloorPlanEditorPage from './screens/host-experience/host-spaces/FloorPlanEditorPage';
import HostPricingPage from "./screens/host-experience/host-pricing";
import HostBillingManagement from "./screens/host-experience/host-billing-management";
import HostSupport from "./screens/host-experience/host-support";

import HostAddSpace from "./screens/host-experience/host-add-space";
import HostPaymentSetup from "./screens/host-experience/host-payment-setup";
import HostAnnouncements from "./screens/host-experience/host-announcements";
import HostSubscriptionUpgrade from "./screens/host-experience/host-subscription-upgrade";
import InvoicePaymentPage from "./screens/host-experience/host-payments/InvoicePaymentPage";
import ServiceCategoriesPage from "./screens/service-categories";
import OffersDiscounts from "./screens/offers-discounts";
import HostOffersDiscounts from "./screens/host-experience/host-offers-discounts";
import PaymentConfiguration from "./screens/payment-configuration";
import PolicyManagement from "./screens/admin/PolicyManagement";
import ApiSettings from "./screens/host-experience/host-settings/ApiSettings";
import RolesPage from "./screens/host-experience/host-settings/Roles";
import Branding from "./screens/host-experience/host-settings/Branding";
import SessionMonitor from "./components/SessionMonitor";
import ScrollToTop from "./components/ScrollToTop";
import LocationPermissionBanner from "./components/LocationPermissionBanner";

// Host Customer routes
import HostCustomerBookSpace from "./screens/host-experience/host-customer-book-space";
import HostCustomerMyBookings from "./screens/host-experience/host-customer-my-bookings";
import HostCustomerFloorPlan from "./screens/host-experience/host-customer-floor-plan";
import HostCustomerDashboard from "./screens/host-experience/host-customer-dashboard";
import HostCustomerProfile from "./screens/host-experience/host-customer-profile";
// import HostCustomerHelp from "./screens/host-experience/host-customer-help";
import SpaceDetail from "./screens/host-experience/host-customer-space-detail";

// Layouts
import { PublicLayout } from "./components/layouts/PublicLayout";
import { AuthenticatedLayout } from "./components/layouts/AuthenticatedLayout";
import HostAuthenticatedLayout from "./components/layouts/HostAuthenticatedLayout";
import SubscriptionWall from "./components/SubscriptionWall";
import PaymentSetupWall from "./components/PaymentSetupWall";

// Missing components
import SelectPackage from "./screens/select-package";
import FeedCreatePost from "./screens/host-experience/host-feed/CreatePost";
import Feed from "./screens/host-experience/host-feed";
import HostPlayServices from "./screens/host-experience/host-play-services";
import CreateListingFlow from "./screens/host-experience/host-play-services/create-listing-flow";
// Import test utilities in development
if (process.env.NODE_ENV === "development") {
  import("./utils/testMessageHandler");
}

// Lazy load FloorPlanEditorPage to avoid import issues
const FloorPlanEditorPage = React.lazy(() => import("./screens/host-experience/host-spaces/FloorPlanEditorPage"));

const { store, persistor } = configureStore();

function DeepLinkGate({ children }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const action = (params.get("action") || "").toLowerCase();
  const secret = params.get("code") || params.get("token");

  if (action && secret) {
    if (["reset_password", "resetpassword", "forgot_password", "forgotpassword", "savepassword"].includes(action)) {
      logger.log("[App] Deep link Redirect: /savepassword", { action });
      return <Redirect to={`/savepassword?code=${secret}`} />;
    }
    if (["confirm_account", "confirmaccount"].includes(action)) {
      logger.log("[App] Deep link Redirect: /otp-confirmation", { action });
      return <Redirect to={`/otp-confirmation?code=${secret}`} />;
    }
    if (["verify_email", "verifyemail", "change_email", "verify", "verify_account"].includes(action)) {
      logger.log("[App] Deep link Redirect: /verify-email", { action });
      return <Redirect to={`/verify-email?code=${secret}`} />;
    }
    if (["invite_confirmation", "confirm_invite"].includes(action)) {
      logger.log("[App] Deep link Redirect: /authentication/confirm-invite", { action });
      return <Redirect to={`/authentication/confirm-invite?code=${secret}`} />;
    }
  }

  return children;
}

function DomainRedirectGate({ children }) {
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";

  // If on enterprise domain in a browser environment, redirect to standard domain
  // This allows enterprise domains to serve as deep link triggers for mobile
  // while keeping the web experience on the primary domain.
  if (hostname.startsWith("enterprise-app.")) {
    const targetDomain = getCanonicalWebDomain();

    // Only redirect if target is different (avoid infinite loops)
    if (hostname !== targetDomain) {
      const newUrl = new URL(window.location.href);
      newUrl.hostname = targetDomain;

      logger.log(`[DomainRedirect] Redirecting from ${hostname} to ${targetDomain}`);
      window.location.replace(newUrl.toString());

      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Redirecting...</span>
            </div>
            <p className="mt-4 text-secondary">Redirecting to secure portal...</p>
          </div>
        </div>
      );
    }
  }

  return children;
}

const App = ({ browserSupport }) => {
  const [error, setError] = useState(null);

  useEffect(() => {
    logger.log("[App] Browser support:", browserSupport);

    if (!browserSupport.hasServiceWorker) {
      setError({
        type: "serviceWorker",
        browser: browserSupport.isFirefox ? "Firefox" : "this browser",
      });
      return;
    }

    // Initialize PostHog Analytics, Feature Flags & A/B Testing
    initPostHog();

    // Re-identify existing logged-in user on session restore / page reload
    const existingUser = localService.getUser();
    if (existingUser) {
      identifyUser(existingUser);
    }

    // Apply Montserrat to all elements
    applyMontserratToAllElements();

    return () => {
      // clearInterval(intervalId);
    };
  }, [browserSupport]);

  if (error) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "600px",
            background: "white",
            borderRadius: "20px",
            padding: "40px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontSize: "28px",
              fontWeight: "600",
              color: "#1a1a1a",
              marginBottom: "10px",
            }}
          >
            Service Worker Not Supported
          </h1>
          <p
            style={{
              fontSize: "16px",
              color: "#666",
              maxWidth: "400px",
              lineHeight: "1.6",
            }}
          >
            Some features may not work properly in this browser mode. Please use a regular window for the best
            experience.
          </p>
        </div>
      </div>
    );
  }

  // Normal render
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <APIProvider apiKey={Constants.GOOGLE_MAPS_API_KEY} version="quarterly" libraries={["places"]}>
          <Router>
            <AnalyticsProvider>
              <SnackbarProvider
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                autoHideDuration={3000}
                maxSnack={2}
              >
                <GlobalStyles />
                <ScrollToTop />
                <SessionMonitor />
                <LocationPermissionBanner />
                <DomainRedirectGate>
                  <DeepLinkGate>
                    <Switch>
                      {/* Public routes */}
                      <Route exact path="/tribute">
                        <Redirect to="/home?tribute" />
                      </Route>
                      <Route exact path="/home">
                        <PublicLayout>
                          <NewHomeScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/home-legacy">
                        <PublicLayout>
                          <LandingWeb />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/work">
                        <PublicLayout>
                          <WorkLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/stay">
                        <PublicLayout>
                          <StayLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/play">
                        <PublicLayout>
                          <PlayLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/eat">
                        <PublicLayout>
                          <EatLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/pricing">
                        <PublicLayout>
                          <PricingLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/enterprise-ready">
                        <PublicLayout>
                          <EnterpriseReadyScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/platform">
                        <PublicLayout>
                          <PlatformLandingScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/move">
                        <PublicLayout>
                          <MoveLandingScreen />
                        </PublicLayout>
                      </Route>
                      {/* Public space detail page replaced by ReusableBookingModal on search/filter views */}
                      {/* <Route exact path={["/space/:spaceId", "/spaces/:spaceId"]}>
                        <PublicLayout>
                          <SpaceDetailPage />
                        </PublicLayout>
                      </Route> */}
                      <Route exact path={["/space/:spaceId", "/spaces/:spaceId"]}>
                        {({ match }) => {
                          const user = localService.getUser();
                          const spaceId = match?.params?.spaceId;
                          if (user) {
                            return <Redirect to={`/filterprops?spaceId=${spaceId}`} />;
                          }
                          return <Redirect to={`/spaces?spaceId=${spaceId}`} />;
                        }}
                      </Route>
                      <Route exact path="/hosts/:hostId">
                        {() => {
                          const user = localService.getUser();
                          if (!user) {
                            return (
                              <PublicLayout>
                                <HostProfilePage />
                              </PublicLayout>
                            );
                          }
                          if (localService.shouldSeeHostExperience()) {
                            return (
                              <HostAuthenticatedLayout>
                                <HostProfilePage />
                              </HostAuthenticatedLayout>
                            );
                          }
                          return (
                            <AuthenticatedLayout>
                              <HostProfilePage />
                            </AuthenticatedLayout>
                          );
                        }}
                      </Route>
                      <Route exact path={["/spaces", "/workspaces-search"]}>
                        {() => {
                          // Show without navbar/footer for both authenticated and unauthenticated users
                          return <WorkspacesSearch />;
                        }}
                      </Route>
                      <Route exact path="/africa-tech-festival">
                        <PublicLayout>
                          <AfricaTechFestivalPage />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/experience/:id">
                        <PublicLayout>
                          <ExperienceDetail />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/host-experience">
                        <PublicLayout>
                          <HostLandingWeb />
                        </PublicLayout>
                      </Route>

                      <Route exact path="/report-issue">
                        <PublicLayout>
                          <ReportIssue />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/corporate-page">
                        <PublicLayout>
                          <CorporatePage />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/qr-code">
                        <PublicLayout>
                          <QRCodeScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/app-redirect">
                        <AppRedirectScreen />
                      </Route>
                      <Route exact path="/qr-demo">
                        <PublicLayout>
                          <QRDemoScreen />
                        </PublicLayout>
                      </Route>
                      <Route exact path="/booking/:bookingId">
                        <BookingDetails />
                      </Route>
                      {/* Invoice payment route - accessible from email links */}
                      <Route exact path="/pay-invoice/:reference">
                        <InvoicePaymentPage />
                      </Route>
                      {/* Auth pages that also need AuthLayout for consistency */}
                      <Route exact path="/signup">
                        <AuthLayout showNavbar={false}>
                          <SignUp />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/signin">
                        <AuthLayout showNavbar={false} showFooter={false} showLogo={false}>
                          <Login />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/confirm">
                        <AuthLayout showNavbar={false}>
                          <Confirm />
                        </AuthLayout>
                      </Route>
                      <Route path="/package-signup">
                        <AuthLayout showNavbar={false}>
                          <PackageSignup />
                        </AuthLayout>
                      </Route>
                      {/* Partner sign-up: same flow, host account types only */}
                      <Route path="/partner-signup">
                        <AuthLayout showNavbar={false}>
                          <PackageSignup hostOnly />
                        </AuthLayout>
                      </Route>
                      <Route path="/signup-success">
                        <AuthLayout showNavbar={false} showFooter={true}>
                          <SignupSuccess />
                        </AuthLayout>
                      </Route>
                      <Route path="/otp-confirmation">
                        <AuthLayout showNavbar={false}>
                          <OTPConfirmation />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/subscriber/pricing">
                        <AuthLayout showNavbar={false} showFooter={true}>
                          <SubscriberPricingPage />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/subscription-upgrade">
                        <AuthLayout showNavbar={false} showFooter={true}>
                          <SubscriberSubscriptionUpgrade />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/payment-redirect">
                        <AuthLayout showNavbar={false} showFooter={false}>
                          <PaymentRedirect />
                        </AuthLayout>
                      </Route>
                      <Route path="/resetpassword">
                        <AuthLayout showNavbar={false}>
                          <ResetPassword />
                        </AuthLayout>
                      </Route>
                      <Route path="/password-rotation">
                        <AuthLayout showNavbar={false}>
                          <PasswordRotation />
                        </AuthLayout>
                      </Route>
                      <Route path="/savepassword">
                        <AuthLayout showNavbar={false}>
                          <SavePassword />
                        </AuthLayout>
                      </Route>
                      <Route path="/gotomail">
                        <AuthLayout showNavbar={false}>
                          <GoToMail />
                        </AuthLayout>
                      </Route>
                      <Route path="/verify-email">
                        <AuthLayout showNavbar={false}>
                          <VerifyEmail />
                        </AuthLayout>
                      </Route>
                      <Route path="/privacy-policy">
                        {/* <PublicLayout> */}
                        <PrivacyPolicy />
                        {/* </PublicLayout> */}
                      </Route>
                      <Route path="/terms-of-use">
                        {/* <PublicLayout> */}
                        <TermsofUse />
                        {/* </PublicLayout> */}
                      </Route>
                      <Route path="/authentication/confirm-invite">
                        <AuthLayout showNavbar={false}>
                          <ConfirmInvite />
                        </AuthLayout>
                      </Route>
                      <Route exact path="/select-package">
                        <AuthLayout showNavbar={false}>
                          <SelectPackage />
                        </AuthLayout>
                      </Route>

                      {/* Host Experience routes - using the individual components */}
                      <Route exact path="/host">
                        {() => {
                          const user = localService.getUser();
                          if (!user) {
                            return <Redirect to="/signin" />;
                          }

                          // Use utility function to get appropriate redirect path
                          const redirectPath = localService.getRedirectPathForUser();
                          return <Redirect to={redirectPath} />;
                        }}
                      </Route>
                      {/* Host Experience Routes */}
                      <Route path="/host/:path?">
                        {() => {
                          const user = localService.getUser();
                          if (!user) {
                            return <Redirect to="/signin" />;
                          }
                          if (!localService.shouldSeeHostExperience()) {
                            return <Redirect to={localService.getRedirectPathForUser() || "/startbooking"} />;
                          }
                          return (
                            <PaymentSetupWall>
                              <SubscriptionWall>
                                <HostAuthenticatedLayout>
                              <Switch>
                                <Route exact path="/host/dashboard" component={HostDashboard} />
                                <Route exact path="/host/analytics" component={HostAnalytics} />
                                <Route exact path="/host/spaces" component={HostAllSpaces} />
                                <Route exact path="/host/play-services/create" component={CreateListingFlow} />
                                <Route exact path="/host/play-services/edit/:id" component={CreateListingFlow} />
                                <Route exact path="/host/play-services" component={HostPlayServices} />
                                <Route exact path="/host/clients" component={HostClients} />
                                <Route exact path="/host/calendar" component={HostCalendar} />
                                <Route exact path="/host/messages" component={HostMessages} />
                                <Route exact path="/host/community" component={HostCommunity} />
                                <Route exact path="/host/invoice/list" component={HostInvoiceList} />
                                <Route exact path="/host/invoice" component={HostInvoice} />
                                <Route exact path="/host/add-invoice" component={AddInvoice} />
                                <Route exact path="/host/invoice/:id" component={ViewInvoice} />
                                <Route exact path="/host/notifications" component={HostNotifications} />
                                <Route exact path="/host/announcements" component={HostAnnouncements} />
                                <Route
                                  exact
                                  path={[
                                    "/host/settings",
                                    "/host/settings-integrations",
                                    "/host/settings-addons",
                                    "/host/settings-building-addresses",
                                    "/host/settings-payment-setup",
                                  ]}
                                  component={HostSettings}
                                />
                                <Route exact path="/host/payment-setup" component={HostPaymentSetup} />
                                <Route exact path="/host/settings-notifications" component={SettingsNotification} />
                                <Route exact path="/host/api-settings" component={ApiSettings} />
                                <Route exact path="/host/roles-settings" component={RolesPage} />
                                <Route exact path="/host/branding-settings" component={Branding} />
                                <Route exact path="/host/pricing" component={HostPricingPage} />
                                <Route exact path="/host/billing-management" component={HostBillingManagement} />
                                <Route exact path="/host/subscription-upgrade" component={HostSubscriptionUpgrade} />
                                <Route exact path="/host/support" component={HostSupport} />

                                <Route exact path="/host/offers-discounts" component={HostOffersDiscounts} />
                                <Route exact path="/host/add-space/:spaceId?" component={HostAddSpace} />
                                <Route exact path="/host/floor-plan-editor/:spaceTitle">
                                  <React.Suspense fallback={<div>Loading Floor Plan Editor...</div>}>
                                    <FloorPlanEditorPage />
                                  </React.Suspense>
                                </Route>
                                <Route exact path="/host/feed/create" component={FeedCreatePost} />
                                <Route exact path="/host/feed" component={Feed} />
                                <Route exact path="/host/book-space" component={HostCustomerBookSpace} />
                                <Route exact path="/host/floor-plan" component={HostCustomerFloorPlan} />
                                <Route exact path="/host/my-bookings" component={HostCustomerMyBookings} />
                                <Route exact path="/host/customer-dashboard" component={HostCustomerDashboard} />
                                <Route exact path="/host/customer-profile" component={HostCustomerProfile} />
                                {/* <Route exact path="/host/customer-help" component={HostCustomerHelp} /> */}
                                <Route exact path="/host/space/:spaceId" component={SpaceDetail} />

                                {/* Host root redirect */}
                                <Route exact path="/host">
                                  {() => {
                                    const user = localService.getUser();
                                    if (!user) return <Redirect to="/signin" />;
                                    return <Redirect to={localService.getRedirectPathForUser()} />;
                                  }}
                                </Route>
                              </Switch>
                            </HostAuthenticatedLayout>
                          </SubscriptionWall>
                        </PaymentSetupWall>
                      );
                    }}
                  </Route>

                      {/* Authenticated routes */}
                      <Route
                        path={[
                          "/dashboard",
                          "/spaces-and-services",
                          "/user-management",
                          "/settings",
                          "/billing",
                          "/billing-management",
                          "/booking",
                          "/disabledHosts",
                          "/companyApprovals",
                          "/approvedHosts",
                          "/suspendedHosts",
                          "/api-explorer",
                          "/roles-permissions",
                          "/service-categories",
                          "/offers-discounts",
                          "/payment-configuration",
                          "/policy-management",
                          "/startbooking",
                          "/reservations",
                          "/orders",
                          "/packages",
                          "/announcements",
                          "/filterprops",
                          "/membership-plan",
                          "/package-order-summary",
                          "/list-property-form",
                        ]}
                      >
                        <SubscriptionWall>
                          <AuthenticatedLayout>
                            <Switch>
                              <Route exact path="/dashboard">
                                {() => {
                                  const user = localService.getUser();
                                  if (!user) return <Redirect to="/signin" />;
                                  if (localService.shouldSeeHostExperience()) return <Redirect to="/host/dashboard" />;
                                  return <Dashboard />;
                                }}
                              </Route>
                              <Route exact path="/spaces-and-services">
                                {() => {
                                  const user = localService.getUser();
                                  if (!user) return <Redirect to="/signin" />;
                                  if (localService.shouldSeeHostExperience()) return <Redirect to="/host/spaces" />;
                                  return <Properties />;
                                }}
                              </Route>
                              <Route exact path="/user-management">
                                <RoleBasedRoute path="/user-management" component={UserManagement} />
                              </Route>
                              <Route exact path="/settings" component={Settings} />
                              <Route exact path="/settings/security" component={Settings} />
                              <Route exact path="/settings/company/profile" component={Settings} />
                              <Route exact path="/settings/groups" component={Settings} />
                              <Route exact path="/settings/amenities" component={Settings} />
                              <Route exact path="/settings/revenue-share" component={Settings} />
                              <Route exact path="/settings/danger-zone" component={Settings} />
                              <Route exact path="/settings/momopayment" component={Settings} />
                              <Route exact path="/billing" component={Billing} />
                              <Route exact path="/billing-management" component={BillingManagement} />
                              <Route exact path="/booking" component={Booking} />
                              <Route exact path="/disabledHosts">
                                <RoleBasedRoute path="/disabledHosts" component={DisabledHosts} />
                              </Route>
                              <Route exact path="/companyApprovals">
                                <RoleBasedRoute path="/companyApprovals" component={CompanyApprovals} />
                              </Route>
                              <Route exact path="/approvedHosts">
                                <RoleBasedRoute path="/approvedHosts" component={ApprovedHosts} />
                              </Route>
                              <Route exact path="/suspendedHosts">
                                <RoleBasedRoute path="/suspendedHosts" component={SuspendedHosts} />
                              </Route>
                              <Route exact path="/api-explorer" component={ApiExplorer} />
                              <Route exact path="/roles-permissions">
                                <RoleBasedRoute path="/roles-permissions" component={RolesAndPermissions} />
                              </Route>
                              <Route exact path="/service-categories">
                                <RoleBasedRoute path="/service-categories" component={ServiceCategoriesPage} />
                              </Route>
                              <Route exact path="/offers-discounts">
                                <RoleBasedRoute path="/offers-discounts" component={OffersDiscounts} />
                              </Route>
                              <Route exact path="/payment-configuration">
                                <RoleBasedRoute path="/payment-configuration" component={PaymentConfiguration} />
                              </Route>
                              <Route exact path="/policy-management">
                                <RoleBasedRoute path="/policy-management" component={PolicyManagement} />
                              </Route>
                              <Route exact path="/startbooking" component={StartBooking} />
                              <Route exact path="/reservations" component={Booking} />
                              <Route exact path="/orders">
                                <RoleBasedRoute path="/orders" component={Orders} />
                              </Route>
                              <Route exact path="/packages">
                                <RoleBasedRoute path="/packages" component={Packages} />
                              </Route>
                              <Route exact path="/announcements">
                                <RoleBasedRoute path="/announcements" component={Announcements} />
                              </Route>
                              <Route exact path="/filterprops" component={FilterProperties} />
                              <Route exact path="/membership-plan">
                                <RoleBasedRoute path="/membership-plan" component={MembershipPlan} />
                              </Route>
                              <Route exact path="/package-order-summary" component={PackageOrderSummary} />
                            </Switch>
                          </AuthenticatedLayout>
                        </SubscriptionWall>
                      </Route>

                      {/* Root path redirect based on authentication */}
                      <Route exact path="/">
                        {() => {
                          // Normal flow - redirect based on auth state
                          const user = localService.getUser();
                          if (!user) {
                            return <Redirect to="/home" />;
                          }

                          // Use utility function to get appropriate redirect path
                          const redirectPath = localService.getRedirectPathForUser();
                          return <Redirect to={redirectPath} />;
                        }}
                      </Route>

                      {/* 404 route */}
                      <Route exact path="/404">
                        <PublicLayout>
                          <NotFoundPage />
                        </PublicLayout>
                      </Route>

                      {/* Default to 404 page if no routes match */}
                      <Route path="*">
                        <PublicLayout>
                          <NotFoundPage />
                        </PublicLayout>
                      </Route>
                    </Switch>
                  </DeepLinkGate>
                </DomainRedirectGate>
              </SnackbarProvider>
            </AnalyticsProvider>
          </Router>
        </APIProvider>
      </PersistGate>
      <Toaster />
    </Provider>
  );
};

export default App;

// https://sandbox.spacia.page/users/v1.1/process/payment/response?references=GTDKMLUC
// add user id to subscription callback. BookingOrderSummary
